import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { ThemeService } from '../../../../core/services/theme';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ProductDetail,
  ProductType,
  StatusUtils,
} from '../../models/product.interface';
import { ProductService } from '../../services/product.service';

@Component({
  selector: 'app-product-detail',
  standalone: true,
  imports: [CommonModule, ButtonModule, TranslateModule],
  templateUrl: './product-detail.component.html',
  styleUrl: './product-detail.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductDetailComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly productService = inject(ProductService);
  private readonly translateService = inject(TranslateService);
  protected readonly themeService = inject(ThemeService);

  productDetail = signal<ProductDetail | null>(null);
  isLoading = signal(true);
  error = signal<string | null>(null);
  productType = signal<ProductType>('ordinary');

  ngOnInit(): void {
    this.setupRouteListener();
    this.loadProductDetail();
  }

  private setupRouteListener(): void {
    this.route.params.subscribe((params) => {
      const type = params['type'] as ProductType;
      if (type === 'package' || type === 'ordinary') {
        this.productType.set(type);
      } else {
        this.productType.set('ordinary');
      }
    });
  }

  loadProductDetail(): void {
    this.route.params.subscribe((params) => {
      const productId = params['product_id'];
      this.isLoading.set(true);
      this.error.set(null);

      this.productService
        .getProductDetail(parseInt(productId), this.productType())
        .subscribe({
          next: (product) => {
            this.productDetail.set(product);
            this.isLoading.set(false);
          },
          error: (error) => {
            console.error('Error loading product detail:', error);
            this.error.set(
              error.message ||
                this.translateService.instant(
                  'goods.product_review.detail.messages.failed_to_load'
                )
            );
            this.isLoading.set(false);
          },
        });
    });
  }

  onReturn(): void {
    window.history.back();
  }

  getStatusLabel(status?: number): string {
    if (status === undefined || status === null) {
      return this.translateService.instant(
        'goods.product_review.detail.values.na'
      );
    }

    // Convert backend status (0-4) to frontend status (1-5) for display
    const frontendStatus = StatusUtils.convertToFrontendStatus(status as any);
    const statusLabel = StatusUtils.getStatusLabel(frontendStatus);
    
    // Map status labels to i18n keys
    let i18nKey = '';
    if (frontendStatus === 2 || frontendStatus === 4) {
      i18nKey = 'goods.product_review.status.under_review';
    } else if (frontendStatus === 3) {
      i18nKey = 'goods.product_review.status.completed';
    } else if (frontendStatus === 1 || frontendStatus === 5) {
      i18nKey = 'goods.product_review.status.rejected';
    }
    
    return i18nKey
      ? this.translateService.instant(i18nKey)
      : statusLabel;
  }
}
